/**
 * Danışanın paketleri, kalan seansı ve tahsilatı.
 *
 * Bakiye PARA BİRİMİ BAŞINA hesaplanıyor. Farklı birimleri toplamak
 * yanlış sonuç verir ve kimse fark etmez; merkez isterse TRY ve EUR
 * paketi birlikte satabiliyor, o zaman iki ayrı bakiye görüyor.
 *
 * Kalan seans sayısını uygulama tutmuyor: seans bir pakete bağlandığında
 * veritabanı tetikleyicisi düşüyor, bağ kaldırıldığında iade ediyor.
 * Elle takip edilen bir sayaç er geç tutmaz.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  ODEME_YONTEMI,
  api,
  paraYaz,
  type Hesap as HesapTipi,
  type Paket,
} from '../api'

function gun(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
}

export function Hesap({
  danisanId,
  yazabilir,
  onGeri,
  onBelgeler,
}: {
  danisanId: string
  yazabilir: boolean
  onGeri: () => void
  onBelgeler: () => void
}) {
  const [hesap, setHesap] = useState<HesapTipi | null>(null)
  const [paketler, setPaketler] = useState<Paket[]>([])
  const [hata, setHata] = useState<string | null>(null)
  const [satisAcik, setSatisAcik] = useState(false)
  const [odemeAcik, setOdemeAcik] = useState(false)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const [h, p] = await Promise.all([api.hesap(danisanId), api.paketler()])
      setHesap(h)
      setPaketler(p.paketler)
    } catch {
      setHata('Hesap açılamadı.')
    }
  }, [danisanId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (hata && !hesap) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <button onClick={onGeri}>← Geri</button>
      </>
    )
  }
  if (!hesap) return <p className="alt">Yükleniyor…</p>

  const bugun = new Date().toISOString().slice(0, 10)

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Dosya</button>
      </div>

      <h1>Paket ve ödeme</h1>
      {hata && <div className="uyari">{hata}</div>}

      <div className="kart">
        {Object.keys(hesap.bakiye).length === 0 ? (
          <p style={{ margin: 0 }}>Bu dosyada henüz satış ya da tahsilat yok.</p>
        ) : (
          Object.entries(hesap.bakiye).map(([pb, b]) => (
            <div key={pb} className="alan-olcut" style={{ marginTop: 0 }}>
              <span>
                Satış: <strong style={{ color: 'var(--ink)' }}>{paraYaz(b.borc, pb)}</strong>
              </span>
              <span>
                Tahsilat: <strong style={{ color: 'var(--ink)' }}>{paraYaz(b.tahsil, pb)}</strong>
              </span>
              <span>
                Kalan:{' '}
                <strong style={{ color: b.kalan > 0 ? 'var(--bad)' : 'var(--ok)' }}>
                  {paraYaz(b.kalan, pb)}
                </strong>
              </span>
            </div>
          ))
        )}
      </div>

      {yazabilir && (
        <div className="dugmeler" style={{ marginBottom: 14 }}>
          <button className="birincil" onClick={() => setSatisAcik((v) => !v)}>
            {satisAcik ? 'Vazgeç' : 'Paket sat'}
          </button>
          <button onClick={() => setOdemeAcik((v) => !v)}>
            {odemeAcik ? 'Vazgeç' : 'Tahsilat ekle'}
          </button>
          <button onClick={onBelgeler}>Makbuz / fatura</button>
        </div>
      )}

      {satisAcik && (
        <SatisFormu
          danisanId={danisanId}
          paketler={paketler}
          onIptal={() => setSatisAcik(false)}
          onKayit={() => {
            setSatisAcik(false)
            void yukle()
          }}
        />
      )}

      {odemeAcik && (
        <OdemeFormu
          danisanId={danisanId}
          hesap={hesap}
          onIptal={() => setOdemeAcik(false)}
          onKayit={() => {
            setOdemeAcik(false)
            void yukle()
          }}
        />
      )}

      <h2>Satılan paketler</h2>
      {hesap.paketler.length === 0 ? (
        <div className="bilgi">Satış yok.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Paket</th>
              <th>Tutar</th>
              <th>Seans</th>
              <th>Satış</th>
              <th>Geçerlilik</th>
              {yazabilir && <th />}
            </tr>
          </thead>
          <tbody>
            {hesap.paketler.map((p) => {
              const kalan = p.toplam - p.kullanilan
              const suresiGecti = Boolean(p.bitis && p.bitis < bugun)
              return (
                <tr key={p.id} style={{ opacity: p.arsiv ? 0.5 : 1 }}>
                  <td>
                    <strong>{p.ad}</strong>
                    {p.not && (
                      <div className="alt" style={{ margin: 0, fontSize: 12 }}>
                        {p.not}
                      </div>
                    )}
                  </td>
                  <td>{paraYaz(p.tutar, p.paraBirimi)}</td>
                  <td>
                    {kalan}/{p.toplam} kaldı
                    {kalan === 0 && (
                      <span className="rozet warn" style={{ marginLeft: 6 }}>
                        bitti
                      </span>
                    )}
                  </td>
                  <td className="alt" style={{ margin: 0 }}>
                    {gun(p.satis)}
                  </td>
                  <td>
                    {p.bitis ? (
                      <span className={suresiGecti ? 'rozet bad' : 'rozet'}>
                        {suresiGecti ? 'süresi doldu' : gun(p.bitis)}
                      </span>
                    ) : (
                      <span className="alt" style={{ margin: 0 }}>
                        süresiz
                      </span>
                    )}
                  </td>
                  {yazabilir && (
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => {
                          void api
                            .satisArsiv(p.id, !p.arsiv)
                            .then(yukle)
                            .catch(() => setHata('İşlem yapılamadı.'))
                        }}
                      >
                        {p.arsiv ? 'Geri al' : 'İptal et'}
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <h2>Tahsilatlar</h2>
      {hesap.odemeler.length === 0 ? (
        <div className="bilgi">Tahsilat yok.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Tutar</th>
              <th>Yöntem</th>
              <th>Not</th>
              {yazabilir && <th />}
            </tr>
          </thead>
          <tbody>
            {hesap.odemeler.map((o) => (
              <tr key={o.id}>
                <td>{gun(o.tarih)}</td>
                <td>{paraYaz(o.tutar, o.paraBirimi)}</td>
                <td>{ODEME_YONTEMI[o.yontem] ?? o.yontem}</td>
                <td className="alt" style={{ margin: 0 }}>
                  {o.not ?? '—'}
                </td>
                {yazabilir && (
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => {
                        void api
                          .odemeArsiv(o.id)
                          .then(yukle)
                          .catch(() => setHata('Geri alınamadı.'))
                      }}
                    >
                      Geri al
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Satış.
 *
 * Tanımlı paket seçilince alanlar dolar ama HEPSİ DEĞİŞTİRİLEBİLİR:
 * indirim, özel anlaşma, farklı seans sayısı. Tanım bir kolaylık,
 * kısıt değil. Paket tanımlamadan doğrudan satış da yapılabiliyor.
 */
function SatisFormu({
  danisanId,
  paketler,
  onIptal,
  onKayit,
}: {
  danisanId: string
  paketler: Paket[]
  onIptal: () => void
  onKayit: () => void
}) {
  const [paketId, setPaketId] = useState('')
  const [ad, setAd] = useState('')
  const [seans, setSeans] = useState(8)
  const [tutar, setTutar] = useState('')
  const [paraBirimi, setParaBirimi] = useState('TRY')
  const [gecerlilik, setGecerlilik] = useState('')
  const [not, setNot] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const sec = (id: string) => {
    setPaketId(id)
    const p = paketler.find((x) => x.id === id)
    if (p) {
      setAd(p.ad)
      setSeans(p.seans)
      setTutar(p.fiyat)
      setParaBirimi(p.paraBirimi)
      setGecerlilik(p.gecerlilikGun ? String(p.gecerlilikGun) : '')
    }
  }

  const gonder = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await api.paketSat(danisanId, {
        ...(paketId ? { paketId } : {}),
        ad: ad.trim(),
        seans,
        tutar: tutar.replace(',', '.'),
        paraBirimi: paraBirimi.trim() || 'TRY',
        gecerlilikGun: gecerlilik ? Number(gecerlilik) : null,
        ...(not.trim() ? { not: not.trim() } : {}),
      })
      onKayit()
    } catch (e2) {
      setHata(
        e2 instanceof ApiHatasi && e2.kod === 'ad_seans_tutar_gerekli'
          ? 'Ad, seans sayısı ve tutar gerekli.'
          : 'Satış kaydedilemedi. Tutarı 1500 ya da 1500.00 gibi yazın.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <form className="kart" onSubmit={gonder}>
      <h2 style={{ marginTop: 0 }}>Paket sat</h2>
      {hata && <div className="uyari">{hata}</div>}
      {paketler.length > 0 && (
        <label>
          Tanımlı paketten doldur
          <select value={paketId} onChange={(e) => sec(e.target.value)}>
            <option value="">Seçiniz (zorunlu değil)</option>
            {paketler.map((p) => (
              <option key={p.id} value={p.id}>
                {p.ad} — {paraYaz(p.fiyat, p.paraBirimi)} / {p.seans} seans
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="satir">
        <label>
          Satılan hizmet
          <input value={ad} onChange={(e) => setAd(e.target.value)} required />
        </label>
        <label>
          Seans sayısı
          <input
            type="number"
            min={1}
            max={200}
            value={seans}
            onChange={(e) => setSeans(Number(e.target.value))}
          />
        </label>
        <label>
          Anlaşılan tutar
          <input value={tutar} onChange={(e) => setTutar(e.target.value)} required />
        </label>
        <label>
          Para birimi
          <input
            value={paraBirimi}
            onChange={(e) => setParaBirimi(e.target.value)}
            maxLength={8}
          />
        </label>
        <label>
          Geçerlilik (gün)
          <input
            type="number"
            min={1}
            max={3650}
            value={gecerlilik}
            onChange={(e) => setGecerlilik(e.target.value)}
            placeholder="boş = süresiz"
          />
        </label>
      </div>
      <label>
        Not
        <input
          value={not}
          onChange={(e) => setNot(e.target.value)}
          placeholder="indirim sebebi, taksit anlaşması"
          maxLength={400}
        />
      </label>
      <div className="dugmeler">
        <button className="birincil" type="submit" disabled={bekliyor}>
          {bekliyor ? 'Kaydediliyor…' : 'Satışı kaydet'}
        </button>
        <button type="button" onClick={onIptal}>
          Vazgeç
        </button>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */

function OdemeFormu({
  danisanId,
  hesap,
  onIptal,
  onKayit,
}: {
  danisanId: string
  hesap: HesapTipi
  onIptal: () => void
  onKayit: () => void
}) {
  const [tutar, setTutar] = useState('')
  const [paraBirimi, setParaBirimi] = useState(
    Object.keys(hesap.bakiye)[0] ?? 'TRY',
  )
  const [yontem, setYontem] = useState<'cash' | 'card' | 'transfer' | 'other'>('cash')
  const [satisId, setSatisId] = useState('')
  const [tarih, setTarih] = useState(() => new Date().toISOString().slice(0, 10))
  const [not, setNot] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const gonder = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await api.odemeEkle(danisanId, {
        tutar: tutar.replace(',', '.'),
        paraBirimi: paraBirimi.trim() || 'TRY',
        yontem,
        satisId: satisId || null,
        tarih: new Date(`${tarih}T12:00:00`).toISOString(),
        ...(not.trim() ? { not: not.trim() } : {}),
      })
      onKayit()
    } catch (e2) {
      setHata(
        e2 instanceof ApiHatasi && e2.kod === 'tutar_pozitif_olmali'
          ? 'Tutar sıfırdan büyük olmalı. İade için ayrı bir kayıt gerekiyor.'
          : 'Tahsilat kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <form className="kart" onSubmit={gonder}>
      <h2 style={{ marginTop: 0 }}>Tahsilat</h2>
      {hata && <div className="uyari">{hata}</div>}
      <div className="satir">
        <label>
          Tutar
          <input value={tutar} onChange={(e) => setTutar(e.target.value)} required />
        </label>
        <label>
          Para birimi
          <input
            value={paraBirimi}
            onChange={(e) => setParaBirimi(e.target.value)}
            maxLength={8}
          />
        </label>
        <label>
          Yöntem
          <select value={yontem} onChange={(e) => setYontem(e.target.value as never)}>
            {Object.entries(ODEME_YONTEMI).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tarih
          <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} />
        </label>
        {hesap.paketler.length > 0 && (
          <label>
            Hangi satış için
            <select value={satisId} onChange={(e) => setSatisId(e.target.value)}>
              <option value="">Belirtilmedi</option>
              {hesap.paketler
                .filter((p) => !p.arsiv)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.ad} — {paraYaz(p.tutar, p.paraBirimi)}
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>
      <label>
        Not
        <input value={not} onChange={(e) => setNot(e.target.value)} maxLength={400} />
      </label>
      <div className="dugmeler">
        <button className="birincil" type="submit" disabled={bekliyor}>
          {bekliyor ? 'Kaydediliyor…' : 'Tahsilatı kaydet'}
        </button>
        <button type="button" onClick={onIptal}>
          Vazgeç
        </button>
      </div>
    </form>
  )
}
